import { Koi } from '@ksyos/koi';
import { CpapClassOption, CpapOption, MraAbcdScore, SptClassOption } from '@common/enums';
import { getTextAreaStringSchema } from '@common/validation/utils/getTextAreaStringSchema';
import { getTextInputStringSchema } from '@common/validation/utils/getTextInputStringSchema';
import { ggzbTherCheckedTreatments } from '../../mentalHealth';
import {
    allTreatmentValidationDraft,
    allTreatmentValidationFinal,
} from '../osasAllTreatmentsSchemas/osasAllTreatmentsSchema';
import { treatmentNames } from '../osasAllTreatmentsSchemas/types';
import { FollowupPerformer } from './types';

export const treatmentValidationDraft = Koi.object({
    CPAP: Koi.alternatives().try(Koi.boolean(), Koi.koi().enum(CpapClassOption).allow(null)),
    MRA: Koi.alternatives().try(Koi.boolean(), Koi.koi().enum(MraAbcdScore).allow(null)),
    SPT: Koi.alternatives().try(Koi.boolean(), Koi.koi().enum(SptClassOption).allow(null)),
    lifestyle: Koi.alternatives().try(Koi.boolean(), getTextAreaStringSchema(true)),
    other: Koi.alternatives().try(Koi.boolean(), getTextAreaStringSchema(true)),
    refer: Koi.boolean(),
});

export const treatmentValidationFinal = Koi.object({
    CPAP: Koi.alternatives().try(Koi.boolean().valid(false), Koi.koi().enum(CpapClassOption).required()),
    MRA: Koi.alternatives().try(Koi.boolean().valid(false), Koi.koi().enum(MraAbcdScore).required()),
    SPT: Koi.alternatives().try(Koi.boolean().valid(false), Koi.koi().enum(SptClassOption).required()),
    lifestyle: Koi.alternatives().try(Koi.boolean().valid(false), getTextAreaStringSchema(false)),
    other: Koi.alternatives().try(Koi.boolean().valid(false), getTextAreaStringSchema(false)),
    refer: Koi.boolean(),
});

export const treatmentValidationNoTreatment = Koi.object({
    CPAP: Koi.boolean().valid(false),
    MRA: Koi.boolean().valid(false),
    SPT: Koi.boolean().valid(false),
    lifestyle: Koi.boolean().valid(false),
    other: Koi.boolean().valid(false),
    refer: Koi.boolean().valid(false),
});

const treatmentFollowupSchema = Koi.object({
    performer: Koi.koi().enum(FollowupPerformer).allow(null),
    weeks: Koi.number().integer().min(1).max(52).allow(null),
});

const treatmentFollowupValidation = Koi.object({
    CPAP: Koi.array().items(treatmentFollowupSchema).allow(null),
    MRA: Koi.array().items(treatmentFollowupSchema).allow(null),
    SPT: Koi.array().items(treatmentFollowupSchema).allow(null),
});

export const policyHasTreatmentFieldsDraft = {
    adviceGeneralPractitionerForOsa: getTextAreaStringSchema(true),
    allTreatments: allTreatmentValidationDraft,
    cpapSetting: Koi.koi().enum(CpapOption).allow(null),
    cpapSettingText: getTextAreaStringSchema(true),
    refer: Koi.object({
        conclusion: getTextAreaStringSchema(true),
        department: getTextInputStringSchema(true, 255),
        message: getTextAreaStringSchema(true),
        organization: getTextInputStringSchema(true, 255),
        question: getTextAreaStringSchema(true),
        reason: getTextAreaStringSchema(true),
        specialismName: getTextInputStringSchema(true, 255),
        subject: getTextInputStringSchema(true, 255),
    }),
    remarkTreatment: getTextAreaStringSchema(true),
    resendQuestionnaires: Koi.boolean().allow(null),
    resendQuestionnairesWeeks: Koi.number().integer().min(1).max(52).allow(null),
    shouldHaveFollowUpConsult: Koi.boolean().allow(null),
    shouldHaveFollowingExamination: Koi.boolean().allow(null),
    taskFollowingExaminationOrConsultWeeks: Koi.number().integer().min(1).max(52).allow(null),
    taskForPoliclinic: getTextAreaStringSchema(true),
    treatment: treatmentValidationDraft,
    treatmentFollowUp: treatmentFollowupValidation,
};

export const treatmentBreakDraftFields = {
    treatmentToBreak: Koi.array()
        .items(Koi.string().valid(...treatmentNames))
        .optional(),
    treatmentToBreakRemark: getTextAreaStringSchema(true),
};

export const treatmentBreakFinalFields = {
    treatmentToBreak: Koi.array()
        .items(Koi.string().valid(...treatmentNames))
        .optional(),
    treatmentToBreakRemark: Koi.when('treatmentToBreak', {
        is: Koi.array().min(1),
        otherwise: getTextAreaStringSchema(true),
        then: getTextAreaStringSchema(false),
    }),
};

export const osaTreatmentRemarksFinalValidation = {
    adviceGeneralPractitionerForOsa: getTextAreaStringSchema(true),
    remarkTreatment: getTextAreaStringSchema(true),
    taskForPoliclinic: getTextAreaStringSchema(true),
};

export const cpapSettingsFieldsFinalValidation = {
    cpapSetting: Koi.when('treatment.CPAP', {
        not: false,
        otherwise: Koi.koi().valid(null),
        then: Koi.koi().enum(CpapOption),
    }),
    cpapSettingText: Koi.when('cpapSetting', {
        is: CpapOption.custom,
        otherwise: Koi.valid(null),
        then: getTextAreaStringSchema(false),
    }),
};

const referTextAreaField = (allowNull?: boolean, max?: number) =>
    Koi.when('...treatment.refer', {
        is: true,
        otherwise: Koi.valid(null),
        then: getTextAreaStringSchema(allowNull, max),
    });

const referTextInputField = (allowNull?: boolean, max?: number) =>
    Koi.when('...treatment.refer', {
        is: true,
        otherwise: Koi.valid(null),
        then: getTextAreaStringSchema(allowNull, max),
    });

const osaReferFinalFields = {
    refer: Koi.object({
        conclusion: referTextAreaField(),
        department: referTextInputField(true, 255),
        message: referTextAreaField(),
        organization: referTextInputField(false, 255),
        question: referTextAreaField(),
        reason: referTextAreaField(),
        specialismName: referTextInputField(false, 255),
        subject: referTextInputField(false, 255),
    }),
};

export const lifeStyleTreatmentFieldsFinalValidation = {
    shouldHaveFollowUpConsult: Koi.when('shouldHaveFollowingExamination', {
        is: false,
        otherwise: Koi.valid(null),
        then: Koi.boolean().required(),
    }),
    shouldHaveFollowingExamination: Koi.when('treatment', {
        is: Koi.object({
            CPAP: Koi.boolean().valid(false),
            MRA: Koi.boolean().valid(false),
            SPT: Koi.boolean().valid(false),
            lifestyle: Koi.alternatives().try(Koi.boolean().valid(true), getTextAreaStringSchema(false)),
            other: Koi.any(),
            refer: Koi.boolean().valid(false),
        }),
        otherwise: Koi.valid(null),
        then: Koi.boolean().required(),
    }),
    taskFollowingExaminationOrConsultWeeks: Koi.when('shouldHaveFollowingExamination', {
        is: true,
        otherwise: Koi.when('shouldHaveFollowUpConsult', {
            is: true,
            otherwise: Koi.valid(null),
            then: Koi.number().integer().min(1).max(52),
        }),
        then: Koi.number().integer().min(1).max(52),
    }),
};

export const policyHasTreatmentFieldsFinal = {
    resendQuestionnaires: Koi.when('hasTreatment', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.boolean(),
    }),
    resendQuestionnairesWeeks: Koi.when('resendQuestionnaires', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.number().integer().min(1).max(52),
    }),

    treatment: Koi.when('hasTreatment', {
        is: true,
        otherwise: treatmentValidationNoTreatment,
        then: treatmentValidationFinal.anyFilledIn(),
    }),

    treatmentFollowUp: treatmentFollowupValidation,
    ...lifeStyleTreatmentFieldsFinalValidation,
    ...osaTreatmentRemarksFinalValidation,
    ...cpapSettingsFieldsFinalValidation,
    ...osaReferFinalFields,
};

export const osasTreatmentChoiceFinalFields = {
    allTreatments: allTreatmentValidationFinal,
    resendQuestionnaires: Koi.boolean(),
    resendQuestionnairesWeeks: Koi.when('resendQuestionnaires', {
        is: true,
        otherwise: Koi.valid(null),
        then: Koi.number().integer().min(1).max(52),
    }),

    treatment: treatmentValidationFinal.anyFilledIn(),
    treatmentFollowUp: treatmentFollowupValidation,
    ...lifeStyleTreatmentFieldsFinalValidation,
    ...osaTreatmentRemarksFinalValidation,
    ...cpapSettingsFieldsFinalValidation,
    ...osaReferFinalFields,
};

export const osasTreatmentChoiceDraft = {
    ...policyHasTreatmentFieldsDraft,
    ...treatmentBreakDraftFields,
    checkedTreatments: ggzbTherCheckedTreatments,
    remarkOther: getTextAreaStringSchema(true),
};

export const osasTreatmentChoiceFinal = {
    ...osasTreatmentChoiceFinalFields,
    ...treatmentBreakFinalFields,
    checkedTreatments: ggzbTherCheckedTreatments,
    remarkOther: getTextAreaStringSchema(true),
};

export const osasTreatmentChoiceFinalSchema = Koi.object(osasTreatmentChoiceFinal);
export const osasTreatmentChoiceDraftSchema = Koi.object(osasTreatmentChoiceDraft);
